import React, { useState, useEffect } from 'react';
import usePageTitle from '../hooks/usePageTitle';

import { useNavigate, useParams } from 'react-router-dom';
import { getARInvoice, deleteARInvoice, submitARInvoice, approveARInvoice, reworkARInvoice, rejectARInvoice, sendARToAccounting, markARInvoiceReceived, markARInvoiceNotReceived, getBankAccounts, getPaymentTypes } from '../utils/api';
import { useAuth } from '../context/AuthContext';
import Modal from '../components/Modal';
import toast from 'react-hot-toast';
import { format } from 'date-fns';
import { ArrowLeftIcon, PencilIcon, TrashIcon, PaperAirplaneIcon, CheckCircleIcon, ArrowPathIcon, XMarkIcon, DocumentArrowUpIcon, ArrowTopRightOnSquareIcon, BanknotesIcon } from '@heroicons/react/24/outline';

const InvoiceDetail = () => {
  usePageTitle('Invoice Details');

  const { id } = useParams();
  const navigate = useNavigate();
  const { user, canApproveInvoices } = useAuth();
  const [invoice, setInvoice] = useState(null);
  const [lineItems, setLineItems] = useState([]);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [commentModalOpen, setCommentModalOpen] = useState(false);
  const [paymentModalOpen, setPaymentModalOpen] = useState(false);
  const [actionType, setActionType] = useState(null);
  const [comment, setComment] = useState('');
  const [paymentData, setPaymentData] = useState({ paid_date: new Date().toISOString().split('T')[0], payment_type: 'bank_transfer', bank_account: '' });
  const [bankAccounts, setBankAccounts] = useState([]);
  const [paymentTypes, setPaymentTypes] = useState([]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { loadInvoice(); loadLookups(); }, [id]);

  const loadLookups = async () => {
    try {
      const [baRes, ptRes] = await Promise.all([getBankAccounts(), getPaymentTypes()]);
      setBankAccounts(baRes.data);
      setPaymentTypes(ptRes.data);
    } catch (e) { console.error(e); }
  };

  const loadInvoice = async () => {
    try {
      const response = await getARInvoice(id);
      setInvoice(response.data.invoice);
      setLineItems(response.data.lineItems || []);
      setHistory(response.data.history || []);
    } catch (error) {
      toast.error('Error loading invoice');
      navigate('/ar-invoices');
    } finally { setLoading(false); }
  };

  const handleDelete = async () => {
    if (!window.confirm('Delete?')) return;
    try { await deleteARInvoice(id); toast.success('Deleted'); navigate('/ar-invoices'); }
    catch (e) { toast.error(e.response?.data?.error || 'Error'); }
  };

  const handleSubmit = async () => {
    setActionLoading(true);
    try { await submitARInvoice(id); toast.success('Submitted'); loadInvoice(); }
    catch (e) { toast.error(e.response?.data?.error || 'Error'); }
    finally { setActionLoading(false); }
  };

  const openCommentModal = (type) => { setActionType(type); setComment(''); setCommentModalOpen(true); };

  const handleActionWithComment = async () => {
    if (actionType === 'reject' && (!comment || comment.trim() === '')) {
      toast.error('Comment is required for rejection');
      return;
    }
    setActionLoading(true);
    try {
      if (actionType === 'approve') { await approveARInvoice(id, comment); toast.success('Approved'); }
      else if (actionType === 'reject') { await rejectARInvoice(id, comment); toast.success('Invoice rejected'); }
      else { await reworkARInvoice(id, comment); toast.success('Sent to rework'); }
      setCommentModalOpen(false); loadInvoice();
    } catch (e) { toast.error(e.response?.data?.error || 'Error'); }
    finally { setActionLoading(false); }
  };

  const handleSendToAccounting = async () => {
    setActionLoading(true);
    try { await sendARToAccounting(id); toast.success('Sent'); loadInvoice(); }
    catch (e) { toast.error(e.response?.data?.error || 'Error'); }
    finally { setActionLoading(false); }
  };

  const handleMarkReceived = async () => {
    if (!paymentData.paid_date) { toast.error('Select date'); return; }
    setActionLoading(true);
    try { await markARInvoiceReceived(id, paymentData); toast.success('Marked paid'); setPaymentModalOpen(false); loadInvoice(); }
    catch (e) { toast.error(e.response?.data?.error || 'Error'); }
    finally { setActionLoading(false); }
  };

  const handleMarkUnpaid = async () => {
    setActionLoading(true);
    try { await markARInvoiceNotReceived(id); toast.success('Marked unpaid'); loadInvoice(); }
    catch (e) { toast.error(e.response?.data?.error || 'Error'); }
    finally { setActionLoading(false); }
  };

  const fc = (amt) => new Intl.NumberFormat('en-US', { style: 'currency', currency: invoice?.currency || 'EUR' }).format(amt || 0);
  
  const getBadge = (t, s) => {
    const c = {
      approval: { draft: ['badge-draft', 'Draft'], pending_approval: ['badge-pending', 'Pending'], approved: ['badge-approved', 'Approved'], rework: ['badge-rework', 'Rework'], rejected: ['badge-rejected', 'Rejected'] },
      accounting: { not_sent: ['badge-draft', 'Not Sent'], sent: ['badge-sent', 'Sent'] },
      payment: { not_paid: ['badge-draft', 'Not Received'], paid: ['badge-approved', 'Received'] },
      overdue: { overdue: ['badge-rework', 'Overdue'], before_due: ['badge-approved', 'On Time'], paid: ['badge-approved', 'Received'] }
    }[t]?.[s] || ['badge-draft', s];
    return <span className={`badge ${c[0]}`}>{c[1]}</span>;
  };

  const canEdit = () => invoice && invoice.approval_status !== 'rejected' && (canApproveInvoices || (invoice.created_by === user.id && ['draft', 'rework'].includes(invoice.approval_status)));
  const canDelete = () => invoice && invoice.approval_status !== 'rejected' && (canApproveInvoices || (invoice.approval_status === 'draft' && invoice.created_by === user.id));
  const canSubmit = () => invoice && ['draft', 'rework'].includes(invoice.approval_status);

  const regularLines = lineItems.filter(li => !li.is_vat_line);
  const vatLines = lineItems.filter(li => li.is_vat_line);
  const subtotal = regularLines.reduce((sum, li) => sum + (parseFloat(li.amount) || 0), 0);
  const vatTotal = vatLines.reduce((sum, li) => sum + (parseFloat(li.amount) || 0), 0);

  if (loading) return <div className="loading-overlay"><div className="spinner" /></div>;
  if (!invoice) return null;

  return (
    <div>
      <div className="page-header">
        <div className="flex items-center gap-md">
          <button className="btn btn-ghost btn-icon" onClick={() => navigate('/ar-invoices')}><ArrowLeftIcon style={{ width: 20, height: 20 }} /></button>
          <div><h1 className="page-title">{invoice.invoice_number}</h1><p className="page-subtitle">{invoice.customer_name}</p></div>
        </div>
        <div className="flex gap-sm">
          {canEdit() && <button className="btn btn-secondary" onClick={() => navigate(`/ar-invoices/${id}/edit`)}><PencilIcon style={{ width: 18, height: 18 }} /> Edit</button>}
          {canDelete() && <button className="btn btn-danger" onClick={handleDelete}><TrashIcon style={{ width: 18, height: 18 }} /> Delete</button>}
        </div>
      </div>

      <div className="card" style={{ marginBottom: 'var(--space-lg)', padding: 'var(--space-md)' }}>
        <div className="flex gap-lg flex-wrap">
          <div><span style={{ fontSize: '0.75rem', color: 'var(--color-text-tertiary)', marginRight: '0.5rem' }}>APPROVAL:</span>{getBadge('approval', invoice.approval_status)}</div>
          <div><span style={{ fontSize: '0.75rem', color: 'var(--color-text-tertiary)', marginRight: '0.5rem' }}>ACCOUNTING:</span>{getBadge('accounting', invoice.accounting_status)}</div>
          <div><span style={{ fontSize: '0.75rem', color: 'var(--color-text-tertiary)', marginRight: '0.5rem' }}>PAYMENT:</span>{getBadge('payment', invoice.payment_status)}</div>
          <div><span style={{ fontSize: '0.75rem', color: 'var(--color-text-tertiary)', marginRight: '0.5rem' }}>DUE:</span>{getBadge('overdue', invoice.overdue_status)}</div>
        </div>
        {invoice.approval_status === 'rejected' && (
          <div style={{ marginTop: 'var(--space-md)', padding: 'var(--space-md)', background: 'rgba(239, 68, 68, 0.1)', borderRadius: 'var(--radius-md)', border: '1px solid rgba(239, 68, 68, 0.3)' }}>
            <div style={{ fontWeight: 600, color: '#ef4444', marginBottom: '0.5rem' }}>This invoice has been rejected and is locked for editing.</div>
            {invoice.rejected_by_name && (
              <div style={{ fontSize: '0.875rem', color: 'var(--color-text-secondary)' }}>
                Rejected by {invoice.rejected_by_name} on {invoice.rejected_at && format(new Date(invoice.rejected_at), 'MMM d, yyyy HH:mm')}
              </div>
            )}
          </div>
        )}
      </div>

      <div className="card" style={{ marginBottom: 'var(--space-lg)' }}>
        <div className="flex gap-md flex-wrap">
          {canSubmit() && <button className="btn btn-primary" onClick={handleSubmit} disabled={actionLoading}><PaperAirplaneIcon style={{ width: 18, height: 18 }} /> Submit</button>}
          {canApproveInvoices && invoice.approval_status === 'pending_approval' && <>
            <button className="btn btn-success" onClick={() => openCommentModal('approve')} disabled={actionLoading}><CheckCircleIcon style={{ width: 18, height: 18 }} /> Approve</button>
            <button className="btn btn-danger" onClick={() => openCommentModal('reject')} disabled={actionLoading}><XMarkIcon style={{ width: 18, height: 18 }} /> Reject</button>
            <button className="btn btn-warning" onClick={() => openCommentModal('rework')} disabled={actionLoading}><ArrowPathIcon style={{ width: 18, height: 18 }} /> Rework</button>
          </>}
          {canApproveInvoices && invoice.approval_status === 'approved' && invoice.accounting_status === 'not_sent' && 
            <button className="btn btn-primary" onClick={handleSendToAccounting} disabled={actionLoading}><DocumentArrowUpIcon style={{ width: 18, height: 18 }} /> Send to Accounting</button>}
          {canApproveInvoices && invoice.payment_status === 'not_received' && 
            <button className="btn btn-success" onClick={() => setPaymentModalOpen(true)} disabled={actionLoading}><BanknotesIcon style={{ width: 18, height: 18 }} /> Mark Received</button>}
          {canApproveInvoices && invoice.payment_status === 'received' && 
            <button className="btn btn-secondary" onClick={handleMarkUnpaid} disabled={actionLoading}><BanknotesIcon style={{ width: 18, height: 18 }} /> Mark Unpaid</button>}
          {invoice.google_drive_link && <a href={invoice.google_drive_link} target="_blank" rel="noopener noreferrer" className="btn btn-secondary"><ArrowTopRightOnSquareIcon style={{ width: 18, height: 18 }} /> View PDF</a>}
        </div>
      </div>

      <div className="invoice-detail-grid">
        <div className="detail-section">
          <div className="detail-section-title">Basic Information</div>
          <div className="detail-row"><span className="detail-label">Invoice #</span><span className="detail-value font-mono">{invoice.invoice_number}</span></div>
          <div className="detail-row"><span className="detail-label">Customer</span><span className="detail-value">{invoice.customer_name}</span></div>
          <div className="detail-row"><span className="detail-label">Reference</span><span className="detail-value">{invoice.reference_number || '-'}</span></div>
          <div className="detail-row"><span className="detail-label">Company</span><span className="detail-value">{invoice.company_name || '-'}</span></div>
          <div className="detail-row"><span className="detail-label">Currency</span><span className="detail-value">{invoice.currency}</span></div>
          <div className="detail-row"><span className="detail-label">Description</span><span className="detail-value">{invoice.description || '-'}</span></div>
        </div>
        <div className="detail-section">
          <div className="detail-section-title">Dates</div>
          <div className="detail-row"><span className="detail-label">Received</span><span className="detail-value">{format(new Date(invoice.issue_date), 'MMM d, yyyy')}</span></div>
          <div className="detail-row"><span className="detail-label">Document</span><span className="detail-value">{format(new Date(invoice.document_date), 'MMM d, yyyy')}</span></div>
          <div className="detail-row"><span className="detail-label">Due</span><span className="detail-value">{format(new Date(invoice.due_date), 'MMM d, yyyy')}</span></div>
          {invoice.pnl_period && (
            <div className="detail-row">
              <span className="detail-label">P&L Period</span>
              <span className="detail-value">
                {format(new Date(invoice.pnl_period), 'MM/yyyy')}
                {invoice.include_in_pnl && <span className="badge badge-approved" style={{ marginLeft: '0.5rem' }}>Included in P&L</span>}
              </span>
            </div>
          )}
          {invoice.paid_date && <div className="detail-row"><span className="detail-label">Received</span><span className="detail-value" style={{ color: 'var(--color-accent-success)' }}>{format(new Date(invoice.paid_date), 'MMM d, yyyy')}</span></div>}
        </div>
        <div className="detail-section">
          <div className="detail-section-title">Payment</div>
          <div className="detail-row"><span className="detail-label">Type</span><span className="detail-value" style={{ textTransform: 'capitalize' }}>{invoice.payment_type?.replace('_', ' ') || '-'}</span></div>
          {invoice.bank_account_name && <div className="detail-row"><span className="detail-label">Bank Account</span><span className="detail-value">{invoice.bank_account_name}</span></div>}
        </div>
        <div className="detail-section">
          <div className="detail-section-title">Totals</div>
          <div className="detail-row"><span className="detail-label">Subtotal</span><span className="detail-value amount">{fc(subtotal)}</span></div>
          {vatTotal > 0 && <div className="detail-row"><span className="detail-label">VAT</span><span className="detail-value amount">{fc(vatTotal)}</span></div>}
          <div className="detail-row"><span className="detail-label">Total</span><span className="detail-value amount" style={{ fontWeight: 700, fontSize: '1.1rem' }}>{fc(invoice.total_amount)}</span></div>
        </div>
      </div>

      {lineItems.length > 0 && (
        <div className="card" style={{ marginTop: 'var(--space-lg)' }}>
          <h3 className="card-title" style={{ marginBottom: 'var(--space-lg)' }}>Line Items</h3>
          <div className="table-container">
            <table className="table">
              <thead><tr><th>#</th><th>Description</th><th>Amount</th><th>Cost Center</th><th>Account</th><th>VAT Ded.</th></tr></thead>
              <tbody>
                {lineItems.map((li) => (
                  <tr key={li.id} style={li.is_vat_line ? { background: 'rgba(245, 158, 11, 0.1)' } : {}}>
                    <td>{li.line_number}</td>
                    <td>{li.description || '-'} {li.is_vat_line && <span className="badge badge-pending" style={{ marginLeft: '0.5rem' }}>VAT</span>}</td>
                    <td className="font-mono">{fc(li.amount)}</td>
                    <td>{li.cost_center_name ? `${li.cost_center_code} - ${li.cost_center_name}` : '-'}</td>
                    <td>{li.account_number ? `${li.account_number} - ${li.account_name}` : '-'}</td>
                    <td>{li.vat_deductible ? <span style={{ color: 'var(--color-accent-success)' }}>Yes</span> : '-'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {history.length > 0 && (
        <div className="card" style={{ marginTop: 'var(--space-lg)' }}>
          <h3 className="card-title" style={{ marginBottom: 'var(--space-lg)' }}>History</h3>
          <div className="timeline">
            {history.map((h) => (
              <div key={h.id} className="timeline-item">
                <div className="timeline-content">
                  <div className="timeline-action">{h.action.replace(/_/g, ' ')}</div>
                  <div className="timeline-meta">{h.performed_by_name} • {format(new Date(h.performed_at), 'MMM d, yyyy HH:mm')}</div>
                  {h.comment && <div className="timeline-comment">"{h.comment}"</div>}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <Modal isOpen={commentModalOpen} onClose={() => setCommentModalOpen(false)}
        title={actionType === 'approve' ? 'Approve Invoice' : actionType === 'reject' ? 'Reject Invoice' : 'Request Rework'}
        footer={<><button className="btn btn-secondary" onClick={() => setCommentModalOpen(false)}>Cancel</button>
          <button className={`btn ${actionType === 'approve' ? 'btn-success' : actionType === 'reject' ? 'btn-danger' : 'btn-warning'}`}
            onClick={handleActionWithComment} disabled={actionLoading}>
            {actionLoading ? '...' : actionType === 'approve' ? 'Approve' : actionType === 'reject' ? 'Reject' : 'Rework'}
          </button></>}>
        <div className="form-group mb-0">
          <label className="form-label">{actionType === 'reject' ? 'Reason for Rejection *' : 'Comment'}</label>
          <textarea className="form-textarea" value={comment} onChange={(e) => setComment(e.target.value)} rows={3}
            placeholder={actionType === 'reject' ? 'Required: Please explain why this invoice is being rejected...' : ''} />
        </div>
      </Modal>

      <Modal isOpen={paymentModalOpen} onClose={() => setPaymentModalOpen(false)} title="Mark as Received"
        footer={<><button className="btn btn-secondary" onClick={() => setPaymentModalOpen(false)}>Cancel</button>
          <button className="btn btn-success" onClick={handleMarkReceived} disabled={actionLoading}>{actionLoading ? '...' : 'Mark Received'}</button></>}>
        <div className="form-group"><label className="form-label">Issue Date *</label><input type="date" className="form-input" value={paymentData.paid_date} onChange={(e) => setPaymentData({ ...paymentData, paid_date: e.target.value })} /></div>
        <div className="form-group"><label className="form-label">Payment Type</label><select className="form-select" value={paymentData.payment_type} onChange={(e) => setPaymentData({ ...paymentData, payment_type: e.target.value })}>
          {paymentTypes.map(pt => <option key={pt.code} value={pt.code}>{pt.name}</option>)}
        </select></div>
        {paymentData.payment_type === 'bank_transfer' && (
          <div className="form-group mb-0"><label className="form-label">Bank Account</label><select className="form-select" value={paymentData.bank_account} onChange={(e) => setPaymentData({ ...paymentData, bank_account: e.target.value })}>
            <option value="">Select</option>{bankAccounts.map(ba => <option key={ba.id} value={ba.id}>{ba.name} ({ba.currency})</option>)}
          </select></div>
        )}
      </Modal>
    </div>
  );
};

export default InvoiceDetail;
